<template>
  <div
    v-if="isLandingEditorPage"
    :class="style.landingEditorSurface"
    data-testid="quiz-settings"
    :data-change-tracking-ready="isChangeTrackingReady"
  >
    <SectionHeaderEditor
      v-if="isChangeTrackingReady"
      :is-can-save="canSaveEditor"
      @save="saveData"
    />

    <LoaderComponent
      v-if="isQuizLoading || !isEditorDataLoaded || !isChangeTrackingReady"
    />

    <LandingPage
      v-if="isEditorDataLoaded"
      :key="landingPageKey"
      :style="landingPageStyle"
    />
  </div>

  <div
    v-else
    :class="style.settingsSurface"
    data-testid="quiz-settings"
    :data-change-tracking-ready="isChangeTrackingReady"
  >
    <LoaderComponent
      v-if="isQuizLoading || !isEditorDataLoaded || !isChangeTrackingReady"
    />

    <div v-else :class="style.settingsContent">
      <ConfigurationQuiz
        v-if="isConfigurationPage"
        @saved="resetChangeTracking"
        @dirty-change="setConfigurationDirty"
      />
      <AnalyticsComponent v-else-if="isAnalyticsPage" />
      <ConfigurationQuiz
        v-else
        @saved="resetChangeTracking"
        @dirty-change="setConfigurationDirty"
      />
    </div>
  </div>

  <ActionModal
    v-model="isUnsavedChangesModalOpen"
    title="Сохранить изменения?"
    subtitle="Вы внесли изменения на странице. Если перейти на другую страницу, они не сохранятся."
    :actions="saveModalsButtons"
  />

  <ToastError
    text="Произошла ошибка. Изменения не сохранены"
    ref="ToastError"
  />

  <ToastSuccess text="Изменения сохранены" ref="ToastSuccess" />
</template>

<script lang="ts">
import {defineComponent} from "vue";

import style from "./QuizSetting.module.scss";

import {modifyQuiz, saveImgQuiz} from "../scriptQuiz";
import {Quiz, questions, section, VersionType, action} from "../typeQuiz";

import {save} from "@/scripts/httpService";

import LandingPage from "./landingPage/LandingPage.vue";
import ConfigurationQuiz from "./configuration/ConfigurationComponent.vue";
import AnalyticsComponent from "./analytics/AnalyticsComponent.vue";
import SectionHeaderEditor from "@/page/quiz/editor/landingPage/components/elements/SectionHeaderEditor/SectionHeaderEditor.vue";
import ToastError from "@/elements/UI/overlay/Toast/Error/ToastError.vue";
import ToastSuccess from "@/elements/UI/overlay/Toast/Success/ToastSuccess.vue";
import ActionModal from "@/layout/nav/components/modal/modals/ActionModal/ActionModal.vue";
import LoaderComponent from "@/elements/UI/overlay/Loader/LoaderComponent.vue";

export default defineComponent({
  name: "QuizSettings",

  components: {
    LoaderComponent,
    ActionModal,
    ToastSuccess,
    ToastError,
    LandingPage,
    ConfigurationQuiz,
    AnalyticsComponent,
    SectionHeaderEditor
  },

  data() {
    return {
      style,

      landingPageKey: 0,

      isEditorDataLoaded: false,
      isChangeTrackingReady: false,

      isCanSave: false,
      isSaving: false,
      cachedFilesSend: true,
      initialQuizSnapshot: "",

      isUnsavedChangesModalOpen: false,
      isRouteLeaveAllowed: false,
      pendingRoutePath: "",
      removeRouteGuard: null as (() => void) | null,
      loadRequestId: 0,

      customUrlValid: true,

      modifyQuiz
    };
  },

  mounted() {
    this.registerRouteGuard();
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    window.addEventListener("keydown", this.handleSaveShortcut);

    this.initEditorPage();
  },

  beforeUnmount() {
    this.removeRouteGuard?.();
    this.removeRouteGuard = null;

    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    window.removeEventListener("keydown", this.handleSaveShortcut);

    localStorage.removeItem("ABTest");
  },

  methods: {
    setConfigurationDirty(isDirty: boolean): void {
      this.isCanSave = isDirty;
    },

    handleSaveShortcut(event: KeyboardEvent): void {
      if (!this.isLandingEditorPage) {
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.code === "KeyS") {
        event.preventDefault();
        void this.saveData();
      }
    },

    registerRouteGuard(): void {
      this.removeRouteGuard?.();

      this.removeRouteGuard = this.$router.beforeEach((to, from, next) => {
        const isOnlyQueryChanged = to.path === from.path;

        if (isOnlyQueryChanged) {
          next();
          return;
        }

        if (this.isRouteLeaveAllowed || !this.canSaveEditor) {
          next();
          return;
        }

        this.pendingRoutePath = to.fullPath;
        this.isUnsavedChangesModalOpen = true;

        next(false);
      });
    },

    handleBeforeUnload(event: BeforeUnloadEvent): void {
      if (!this.canSaveEditor || this.isSaving) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    },

    closeUnsavedChangesModal(): void {
      this.isUnsavedChangesModalOpen = false;
    },

    goToPendingRoute(): void {
      const pendingRoutePath = this.pendingRoutePath;

      this.pendingRoutePath = "";
      this.closeUnsavedChangesModal();

      if (!pendingRoutePath) {
        return;
      }

      this.isRouteLeaveAllowed = true;

      this.$router.push(pendingRoutePath).finally(() => {
        this.isRouteLeaveAllowed = false;
      });
    },

    async leaveWithoutSaving(): Promise<void> {
      if (this.isSaving) {
        return;
      }

      this.$quizStore.clearCachedFiles();

      if (this.initialQuizSnapshot) {
        try {
          const restoredQuiz = JSON.parse(this.initialQuizSnapshot) as Quiz;

          this.$quizStore.setQuiz(restoredQuiz);
        } catch (error) {
          console.error("Ошибка при восстановлении квиза:", error);
        }
      }

      this.isCanSave = false;
      this.cachedFilesSend = true;

      this.goToPendingRoute();
    },

    async saveAndLeave(): Promise<void> {
      if (this.isSaving) {
        return;
      }

      const isSaved = await this.saveData();

      if (!isSaved) {
        return;
      }

      this.goToPendingRoute();
    },

    async initEditorPage(): Promise<void> {
      this.isEditorDataLoaded = false;
      this.isChangeTrackingReady = false;
      this.isCanSave = false;
      this.isSaving = false;
      this.cachedFilesSend = true;
      this.initialQuizSnapshot = "";

      this.$quizStore.setSelectedVersionLK(this.currentVersion);
      await this.$userStore.fetchUser();

      const id = this.$route.params.id;

      if (!Array.isArray(id)) {
        await this.setData(id);
      }
    },

    async setData(id: string): Promise<void> {
      this.isEditorDataLoaded = false;
      this.isChangeTrackingReady = false;
      this.isCanSave = false;
      this.isSaving = false;
      this.cachedFilesSend = true;
      this.initialQuizSnapshot = "";
      const requestId = ++this.loadRequestId;

      try {
        await this.$quizStore.fetchQuiz([id, true]);
      } catch (error) {
        console.error("Ошибка при загрузке квиза:", error);
        this.showToastError();
        return;
      }

      if (requestId !== this.loadRequestId) {
        return;
      }

      if (!this.quiz?._id || !Array.isArray(this.quiz.questions)) {
        this.showToastError();
        return;
      }

      this.ensureQuizStyleDefaults();
      this.ensureMobileQuizDefaults();
      this.normalizeLegacyPhotoRight();
      this.removeTransientQuizFields();

      this.isEditorDataLoaded = true;

      await this.$nextTick();
      await this.waitUntilQuizStable();

      this.resetChangeTracking();
    },

    async waitUntilQuizStable(): Promise<void> {
      let previousSnapshot = "";
      let stableCount = 0;

      for (let index = 0; index < 30; index++) {
        await this.$nextTick();
        await this.wait(50);

        const currentSnapshot = this.getQuizSnapshot();

        if (currentSnapshot === previousSnapshot) {
          stableCount++;
        } else {
          stableCount = 0;
          previousSnapshot = currentSnapshot;
        }

        if (stableCount >= 3) {
          return;
        }
      }
    },

    wait(ms: number): Promise<void> {
      return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
      });
    },

    async handleSaveImages(): Promise<string[]> {
      const files = Array.isArray(this.cachedFiles)
        ? [...this.cachedFiles]
        : [];
      const savedActionPhotoPaths: string[] = [];

      if (files.length === 0) {
        return savedActionPhotoPaths;
      }

      for (const file of files) {
        if (!(file instanceof File)) {
          throw new Error("В cachedFiles лежит не File");
        }

        if (!file.name) {
          throw new Error("У файла из cachedFiles нет имени");
        }

        const response = await saveImgQuiz(file, this.mobileVersion);
        const savedPath = this.getSavedImagePath(response);

        if (!savedPath) {
          throw new Error(`Backend не вернул путь для файла ${file.name}`);
        }

        this.applySavedImagePath(file.name, savedPath);

        if (
          this.getActionFileData(file.name) &&
          this.isImageFile(file, savedPath)
        ) {
          savedActionPhotoPaths.push(savedPath);
        }
      }

      return savedActionPhotoPaths;
    },

    isImageFile(file: File, path: string): boolean {
      if (file.type.startsWith("image/")) {
        return true;
      }

      return /\.(png|jpe?g|webp|gif|svg|avif)(?:$|[?#])/i.test(path);
    },

    getSavedImagePath(response: unknown): string {
      if (typeof response === "string") {
        return response;
      }

      if (
        response &&
        typeof response === "object" &&
        "data" in response &&
        typeof (response as {data?: unknown}).data === "string"
      ) {
        return (response as {data: string}).data;
      }

      return "";
    },

    applySavedImagePath(fileName: string, savedPath: string): void {
      const isActionImageApplied = this.applySavedActionImagePath(
        fileName,
        savedPath
      );

      if (isActionImageApplied) {
        return;
      }

      const isRightSectionImageApplied = this.applySavedRightSectionImagePath(
        fileName,
        savedPath
      );

      if (isRightSectionImageApplied) {
        return;
      }

      this.applySavedGenericImagePath(fileName, savedPath);
    },

    getImageBaseName(path: string): string {
      return (
        String(path || "")
          .split("/")
          .pop() || ""
      );
    },

    isSameImageReference(currentPath: unknown, fileName: string): boolean {
      if (typeof currentPath !== "string" || !currentPath) {
        return false;
      }

      return (
        currentPath === fileName ||
        this.getImageBaseName(currentPath) === fileName
      );
    },

    applySavedGenericImagePath(fileName: string, savedPath: string): boolean {
      if (!fileName || !savedPath) {
        return false;
      }

      if (this.isSameImageReference(this.quiz.logo, fileName)) {
        this.modifyQuiz("set", savedPath, ["logo"]);
        return true;
      }

      if (!Array.isArray(this.quiz.questions)) {
        return false;
      }

      for (
        let questionIndex = 0;
        questionIndex < this.quiz.questions.length;
        questionIndex++
      ) {
        const question = this.quiz.questions[questionIndex];

        if (this.isSameImageReference(question.information, fileName)) {
          this.modifyQuiz("set", savedPath, [
            "questions",
            questionIndex,
            "information"
          ]);
          return true;
        }

        if (this.isSameImageReference(question.thankYouImg, fileName)) {
          this.modifyQuiz("set", savedPath, [
            "questions",
            questionIndex,
            "thankYouImg"
          ]);
          return true;
        }

        if (!Array.isArray(question.variants)) {
          continue;
        }

        for (
          let variantIndex = 0;
          variantIndex < question.variants.length;
          variantIndex++
        ) {
          if (
            !this.isSameImageReference(
              question.variants[variantIndex]?.img,
              fileName
            )
          ) {
            continue;
          }

          this.modifyQuiz("set", savedPath, [
            "questions",
            questionIndex,
            "variants",
            variantIndex,
            "img"
          ]);
          return true;
        }
      }

      return false;
    },

    getActionFileData(fileName: string): {actionIndex: number} | null {
      const baseName =
        String(fileName || "")
          .split("/")
          .pop() || "";

      if (!baseName.includes("action")) {
        return null;
      }

      const match = baseName.match(/^(\d+)-action(?:[.-].*)?\.[^.]+$/);

      if (!match) {
        return null;
      }

      const actionIndex = Number(match[1]);

      if (!Number.isFinite(actionIndex)) {
        return null;
      }

      return {
        actionIndex
      };
    },

    applySavedActionImagePath(fileName: string, savedPath: string): boolean {
      const fileData = this.getActionFileData(fileName);

      if (!fileData || !savedPath) {
        return false;
      }

      this.modifyQuiz("set", savedPath, [
        "actionList",
        fileData.actionIndex,
        "img"
      ]);

      return true;
    },

    getRightSectionFileData(
      fileName: string
    ): {sectionIndex: number; imageIndex: number} | null {
      if (!fileName.includes("photoRightSection")) {
        return null;
      }

      const nameWithoutExtension = fileName.split(".").slice(0, -1).join(".");
      const nameParts = nameWithoutExtension.split("-");

      const sectionIndex = Number(nameParts[0]);
      const imageIndex = Number(nameParts[1]);

      if (!Number.isFinite(sectionIndex) || !Number.isFinite(imageIndex)) {
        return null;
      }

      return {
        sectionIndex,
        imageIndex
      };
    },

    applySavedRightSectionImagePath(
      fileName: string,
      savedPath: string
    ): boolean {
      const fileData = this.getRightSectionFileData(fileName);

      if (!fileData || !savedPath) {
        return false;
      }

      this.modifyQuiz("set", savedPath, [
        "rightSectionList",
        fileData.sectionIndex,
        "img",
        fileData.imageIndex
      ]);

      return true;
    },

    async saveData(): Promise<boolean> {
      const hasCachedFiles = this.cachedFiles.length > 0;

      if ((!this.isCanSave && !hasCachedFiles) || this.isSaving) {
        return false;
      }

      if (!this.quiz?._id) {
        return false;
      }

      if (!Array.isArray(this.quiz.questions)) {
        return false;
      }

      if (!this.initialQuizSnapshot) {
        this.resetChangeTracking();
        return false;
      }

      this.isSaving = true;

      // this.quiz.isActionTopMobile = "fnkdjsnfjksd";

      try {
        this.removeTransientQuizFields();
        this.removeDuplicateQuestionsByContent();
        this.quiz.questions = this.normalizeQuestions(this.quiz.questions);

        this.checkDuplicatesQuiz();

        const response = await save(
          "quiz",
          this.initialQuizSnapshot,
          this.quiz,
          {
            _id: this.quiz._id
          }
        );

        const typedResponse = response as
          | {
              data?: {
                message?: string;
              };
            }
          | undefined;

        this.customUrlValid =
          typedResponse?.data?.message !== "Такая ссылка уже существует";

        this.$quizStore.setCustomUrlValid(this.customUrlValid);

        if (!this.customUrlValid) {
          this.updateSaveState();
          return false;
        }

        this.cachedFilesSend = false;

        const savedActionPhotoPaths = await this.handleSaveImages();

        this.$quizStore.clearCachedFiles();

        this.cachedFilesSend = true;

        await this.$nextTick();

        if (savedActionPhotoPaths.length > 0) {
          window.dispatchEvent(
            new CustomEvent("quiz-saved-action-photos", {
              detail: {
                paths: savedActionPhotoPaths,
                version: Date.now()
              }
            })
          );
        }

        window.dispatchEvent(
          new CustomEvent("quiz-media-saved", {
            detail: {
              version: Date.now()
            }
          })
        );

        this.resetChangeTracking();

        this.showToastSuccess();

        return true;
      } catch (error) {
        this.cachedFilesSend = true;
        console.error("Ошибка при сохранении квиза:", error);
        this.showToastError();

        this.updateSaveState();

        return false;
      } finally {
        this.isSaving = false;
      }
    },

    resetChangeTracking(): void {
      this.removeTransientQuizFields();

      const snapshot = this.getQuizSnapshot();

      this.initialQuizSnapshot = snapshot;
      this.isCanSave = false;
      this.isChangeTrackingReady = true;

      this.$quizStore.setQuizStartedValue(snapshot);
    },

    showToastError(): void {
      const refToastError = this.$refs.ToastError as unknown as
        ToastType | undefined;
      refToastError?.show();
    },

    showToastSuccess(): void {
      const refToastSuccess = this.$refs.ToastSuccess as unknown as
        ToastType | undefined;

      refToastSuccess?.show();
    },

    updateSaveState(): void {
      if (!this.isChangeTrackingReady) {
        return;
      }

      const currentSnapshot = this.getQuizSnapshot();

      this.isCanSave =
        currentSnapshot !== this.initialQuizSnapshot ||
        (this.cachedFiles.length > 0 && this.cachedFilesSend);
    },

    getQuizSnapshot(): string {
      return JSON.stringify(this.normalizeValueForSnapshot(this.quiz));
    },

    normalizeValueForSnapshot(value: unknown): unknown {
      if (value === null || typeof value !== "object") {
        return value;
      }

      if (Array.isArray(value)) {
        return value.map((item) => this.normalizeValueForSnapshot(item));
      }

      if (value instanceof Date) {
        return value.toISOString();
      }

      const source = value as Record<string, unknown>;
      const result: Record<string, unknown> = {};

      Object.keys(source)
        .sort()
        .forEach((key) => {
          if (key === "logoPreview") {
            return;
          }

          const normalizedValue = this.normalizeValueForSnapshot(source[key]);

          if (typeof normalizedValue !== "undefined") {
            result[key] = normalizedValue;
          }
        });

      return result;
    },

    getQuestionDuplicateKey(question: questions): string {
      return JSON.stringify(this.normalizeValueForSnapshot(question));
    },

    removeDuplicateQuestionsByContent(): void {
      if (!Array.isArray(this.quiz?.questions)) {
        return;
      }

      const seenQuestions = new Set<string>();
      const duplicateQuestions: string[] = [];

      this.quiz.questions = this.quiz.questions.filter((question) => {
        const questionKey = this.getQuestionDuplicateKey(question);

        if (seenQuestions.has(questionKey)) {
          duplicateQuestions.push(String(question.question ?? ""));
          return false;
        }

        seenQuestions.add(questionKey);

        return true;
      });

      if (duplicateQuestions.length === 0) {
        return;
      }

      console.error("Найдены полностью одинаковые вопросы:", [
        ...new Set(duplicateQuestions)
      ]);
    },

    normalizeQuestions(arr: questions[]): questions[] {
      let contactsFound = false;
      let thankYouFound = false;

      let normalizedQuestions = arr.filter((question) => {
        if (question.type === "contacts") {
          if (contactsFound) {
            return false;
          }

          contactsFound = true;
          return true;
        }

        if (question.type === "ThankYouPages") {
          if (thankYouFound) {
            return false;
          }

          thankYouFound = true;
          return true;
        }

        return true;
      });

      if (!contactsFound) {
        normalizedQuestions.push({
          type: "contacts",
          question: "Контакты",
          placeholder: "",
          contacts: {},
          paragraph: ""
        });
      }

      if (!thankYouFound) {
        normalizedQuestions.push({
          type: "ThankYouPages",
          question: "Благодарим за ответы на все вопросы",
          placeholder: "",
          contacts: {},
          paragraph: ""
        });
      }

      normalizedQuestions = normalizedQuestions.map((question) => {
        if (question.type === "contacts") {
          return {
            ...question,
            question: "Контакты"
          };
        }

        if (question.type === "ThankYouPages") {
          return {
            ...question,
            question: question.question
              ? question.question
              : "Благодарим за ответы на все вопросы"
          };
        }

        return question;
      });

      normalizedQuestions.sort((a, b) => {
        const order: Record<string, number> = {
          default: 0,
          contacts: 1,
          ThankYouPages: 2
        };

        const getPriority = (question: questions): number => {
          return order[question.type] ?? order.default;
        };

        return getPriority(a) - getPriority(b);
      });

      return normalizedQuestions;
    },

    checkDuplicatesQuiz(): void {
      const quiz = this.quiz as Quiz;

      const dedupeStringsArray = (arr: string[]): string[] => {
        if (!Array.isArray(arr)) {
          return arr;
        }

        const seen = new Set<string>();
        const out: string[] = [];

        arr.forEach((value) => {
          const key = String(value ?? "");

          if (!seen.has(key)) {
            seen.add(key);
            out.push(value);
          }
        });

        return out;
      };

      const normalizeSectionsImgs = (sections?: section[] | section[]) => {
        if (!Array.isArray(sections)) {
          return;
        }

        sections.forEach((item) => {
          if (Array.isArray(item.img)) {
            item.img = dedupeStringsArray(item.img);
          }
        });
      };

      normalizeSectionsImgs(quiz.rightSectionList);

      const seenSections = new Set<string>();

      if (Array.isArray(quiz.rightSectionList)) {
        quiz.rightSectionList = quiz.rightSectionList.filter((item) => {
          const imgsKey = Array.isArray(item.img)
            ? JSON.stringify(item.img)
            : item.img || "";

          const key = `${item.title || ""}||${item.subTitle || ""}||${imgsKey}`;

          if (seenSections.has(key)) {
            return false;
          }

          seenSections.add(key);

          return true;
        });
      }

      normalizeSectionsImgs(quiz.mobileVersionQuiz?.rightSectionList);

      const seenMobileSections = new Set<string>();

      if (
        quiz.mobileVersionQuiz &&
        Array.isArray(quiz.mobileVersionQuiz.rightSectionList)
      ) {
        quiz.mobileVersionQuiz.rightSectionList =
          quiz.mobileVersionQuiz.rightSectionList.filter((item: section) => {
            const imgsKey = Array.isArray(item.img)
              ? JSON.stringify(item.img)
              : item.img || "";

            const key = `${item.title || ""}||${item.subTitle || ""}||${imgsKey}`;

            if (seenMobileSections.has(key)) {
              return false;
            }

            seenMobileSections.add(key);

            return true;
          });
      }

      const seenActions = new Set<string>();

      if (Array.isArray(quiz.actionList)) {
        quiz.actionList = quiz.actionList.filter((item) => {
          const key = `${item.header || ""}||${item.paragraph || ""}||${item.img || ""}`;

          if (seenActions.has(key)) {
            return false;
          }

          seenActions.add(key);

          return true;
        });
      }

      const seenActionsMobile = new Set<string>();

      if (
        quiz.mobileVersionQuiz &&
        Array.isArray(quiz.mobileVersionQuiz.actionList)
      ) {
        quiz.mobileVersionQuiz.actionList =
          quiz.mobileVersionQuiz.actionList.filter((item: action) => {
            const key = `${item.header || ""}||${item.paragraph || ""}||${item.img || ""}`;

            if (seenActionsMobile.has(key)) {
              return false;
            }

            seenActionsMobile.add(key);

            return true;
          });
      }

      if (Array.isArray(quiz.questions)) {
        quiz.questions.forEach((question) => {
          if (
            Array.isArray(question.variants) &&
            question.variants.length > 0
          ) {
            const seenVariants = new Set<string>();

            question.variants = question.variants.filter((variant) => {
              const key = `${variant.img || ""}||${variant.text || ""}`;

              if (seenVariants.has(key)) {
                return false;
              }

              seenVariants.add(key);

              return true;
            });
          }
        });
      }
    },

    ensureQuizStyleDefaults(): void {
      if (!modifyQuiz("get", "", ["styleName"])) {
        modifyQuiz("set", {type: "onlyName"}, ["styleName"]);
      }

      if (!modifyQuiz("get", "", ["styleContacts"])) {
        modifyQuiz("set", {type: "both"}, ["styleContacts"]);
      }
    },

    ensureMobileQuizDefaults(): void {
      const quiz = this.quiz as Quiz;

      if (!quiz.mobileVersionQuiz) {
        quiz.mobileVersionQuiz = {};
      }
    },

    normalizeLegacyPhotoRight(): void {
      const quiz = this.quiz as Quiz;

      if (!quiz.photoRight) {
        return;
      }

      if (
        Array.isArray(quiz.rightSectionList) &&
        quiz.rightSectionList.length > 0
      ) {
        return;
      }

      const photoRightList = Array.isArray(quiz.photoRight)
        ? (quiz.photoRight as section[])
        : ([quiz.photoRight] as section[]);

      quiz.photoRight = photoRightList;

      quiz.rightSectionList = photoRightList.map((item) => {
        const defaultSection: section = {
          img: [],
          title: "",
          subTitle: "",
          countImg: 1,
          height: 100,
          heightMeasurement: "%",
          type: "",
          positioningImg: ""
        };

        if (typeof item === "string") {
          return {
            ...defaultSection,
            img: [item]
          };
        }

        const imgList = Array.isArray(item.img)
          ? item.img
          : item.img
            ? [item.img]
            : [];

        return {
          ...defaultSection,
          ...item,
          img: imgList,
          title: item.title ?? "",
          subTitle: item.subTitle ?? "",
          countImg: item.countImg ?? 1,
          height: item.height ?? 100,
          heightMeasurement:
            item.heightMeasurement === "Px" || item.heightMeasurement === "%"
              ? item.heightMeasurement
              : "%",
          type: item.type ?? "",
          positioningImg: item.positioningImg ?? ""
        };
      });
    },

    removeTransientQuizFields(): void {
      this.removeTransientFieldsFromValue(this.quiz);
    },

    removeTransientFieldsFromValue(value: unknown): void {
      if (!value || typeof value !== "object") {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach((item) => {
          this.removeTransientFieldsFromValue(item);
        });
        return;
      }

      const target = value as Record<string, unknown>;
      const mediaKeys = new Set(["logo", "img", "information", "thankYouImg"]);

      Object.keys(target).forEach((key) => {
        if (key === "logoPreview") {
          delete target[key];
          return;
        }

        const childValue = target[key];

        if (
          mediaKeys.has(key) &&
          typeof childValue === "string" &&
          (childValue.startsWith("blob:") || childValue.startsWith("data:"))
        ) {
          target[key] = "";
          return;
        }

        this.removeTransientFieldsFromValue(childValue);
      });
    },

    routeNameIs(...names: string[]): boolean {
      return names.includes(this.routeName);
    },

    routePathIncludes(...parts: string[]): boolean {
      return parts.some((part) => this.routePath.includes(part));
    }
  },

  computed: {
    quiz(): Quiz {
      return this.$quizStore.getQuiz;
    },

    cachedFiles(): File[] {
      return this.$quizStore.getCachedFiles || [];
    },

    mobileVersion(): boolean {
      return this.currentVersion === "Мобильная";
    },

    canSaveEditor(): boolean {
      return (
        (this.isCanSave ||
          (this.cachedFiles.length > 0 && this.cachedFilesSend)) &&
        !this.isSaving
      );
    },

    isQuizLoading(): boolean {
      return !this.quiz || this.quiz._id === "";
    },

    currentVersion(): VersionType {
      return this.$route.query.version === "Мобильная"
        ? "Мобильная"
        : "Десктоп";
    },

    landingPageStyle(): Record<string, string> {
      return {
        visibility: this.isChangeTrackingReady ? "visible" : "hidden"
      };
    },

    routeName(): string {
      return String(this.$route.name || "");
    },

    routePath(): string {
      return String(this.$route.path || "");
    },

    isLandingEditorPage(): boolean {
      return (
        this.routeNameIs("landingPage", "ABTest", "ABTets") ||
        this.routePathIncludes("/landingPage", "/ABTest", "/ABTets")
      );
    },

    isConfigurationPage(): boolean {
      return (
        this.routeNameIs("configurationQuiz", "ConfigurationQuiz") ||
        this.routePathIncludes("/configuration")
      );
    },

    isAnalyticsPage(): boolean {
      return (
        this.routeNameIs("analyticsQuiz", "AnalyticsComponent") ||
        this.routePathIncludes("/analytics")
      );
    },

    saveModalsButtons(): ListActionItem[] {
      return [
        {
          text: "Не сохранять",
          disabled: this.isSaving,
          handler: this.leaveWithoutSaving
        },
        {
          text: "Сохранить",
          disabled: this.isSaving,
          handler: this.saveAndLeave
        }
      ];
    }
  },

  watch: {
    quiz: {
      handler(): void {
        this.updateSaveState();
      },
      deep: true
    },

    cachedFiles: {
      handler(newCachedFiles: File[]): void {
        if (!this.isChangeTrackingReady) {
          return;
        }

        if (newCachedFiles.length > 0 && this.cachedFilesSend) {
          this.isCanSave = true;
          return;
        }

        this.updateSaveState();
      },
      deep: true
    },

    customUrlValid(): void {
      this.$quizStore.setCustomUrlValid(this.customUrlValid);
    },

    "quiz.customUrl": function (): void {
      this.customUrlValid = true;
    },

    "$route.params.id": async function (): Promise<void> {
      this.isEditorDataLoaded = false;
      this.isChangeTrackingReady = false;
      this.isCanSave = false;
      this.isSaving = false;
      this.cachedFilesSend = true;
      this.initialQuizSnapshot = "";

      await this.$userStore.fetchUser();

      const id = this.$route.params.id;

      this.landingPageKey++;

      if (!Array.isArray(id)) {
        this.$quizStore.resetQuiz();
        this.$quizStore.setSelectedQuestion(-1);
        await this.setData(id);
      }
    },

    "$route.path": function (): void {
      if (this.isLandingEditorPage) {
        this.landingPageKey++;
      }

      this.updateSaveState();
    },

    "$route.query.version": function (): void {
      this.$quizStore.setSelectedVersionLK(this.currentVersion);
      this.landingPageKey++;

      this.updateSaveState();
    }
  }
});
</script>
